import Button from '../../../../../components/Button/Button';
import LinkButton from '../../../../../components/Button/LinkButton';
import Card from '../../../../../components/Card/Card';
import CleanLinkButton from '../../../../../components/CleanLinkButton/CleanLinkButton';
import Column from '../../../../../components/Column/Column';
import Columns from '../../../../../components/Columns/Columns';
import DialogModal, {
    DialogModalProps,
} from '../../../../../components/DialogModal/DialogModal';
import Inline from '../../../../../components/Inline/Inline';
import InlineIcon from '../../../../../components/InlineIcon/InlineIcon';
import PaddedBox from '../../../../../components/PaddedBox/PaddedBox';
import Paragraph from '../../../../../components/Paragraph/Paragraph';
import RichText from '../../../../../components/RichText/RichText';
import Row from '../../../../../components/Row/Row';
import Stack from '../../../../../components/Stack/Stack';
import Title from '../../../../../components/Title/Title';
import useTrans from '../../../../../hooks/ui/useTrans';
import MediaCarousel from '../../../../components/MediaCarousel/MediaCarousel';
import PhoneNumberDisplay from '../../../../components/PhoneNumberDisplay/PhoneNumberDisplay';
import StaticGoogleMap from '../../../../components/StaticGoogleMap/StaticGoogleMap';
import useGoogleMapsUrl from '../../../../hooks/useGoogleMapsUrl';
import DockWithMedia from '../../../../types/DockWithMedia';

import markerIcon from './graphics/marker.svg?raw';

import styles from './DockInfoModal.module.css';

type DockInfoModalProps = {
    dock: DockWithMedia;
    onSelect: (dock: DockWithMedia) => void;
    googleApiKey: string;
} & Omit<DialogModalProps, 'children' | 'width'>;

const DockInfoModal = ({
    dock,
    onSelect,
    googleApiKey,
    ...modalProps
}: DockInfoModalProps) => {
    const trans = useTrans('CustomerArea.BookingForm.DockInfoModal.');
    const mapsUrl = useGoogleMapsUrl(dock.address.gps, 14);
    const handleChooseClick = () => onSelect(dock);
    const hasMedia = dock.media && dock.media.length > 0;

    return (
        <DialogModal
            {...modalProps}
            isOpen
            backdrop="none"
            width="regular"
            unstyled
        >
            <Card horizontalPadding="none" verticalPadding="none">
                {hasMedia && (
                    <div className={styles.Carousel}>
                        <MediaCarousel media={dock.media} variant="fullSize">
                            <Title variant="area" lineHeight="small">
                                {dock.name}
                            </Title>
                        </MediaCarousel>
                    </div>
                )}
                <PaddedBox
                    variant="white"
                    borderRadius={hasMedia ? 'none' : 'top'}
                >
                    <Columns collapseBelow="tablet" gap="xlarge">
                        <Column width="1/2">
                            <Stack>
                                <Row space="small">
                                    <InlineIcon src={markerIcon} />
                                    <Stack space="none" flex>
                                        <Paragraph bold>
                                            {dock.address.line1}
                                            {', '}
                                            {dock.address.city}
                                        </Paragraph>
                                        <Paragraph subdued size="small">
                                            <PhoneNumberDisplay>
                                                {dock.phoneNumber}
                                            </PhoneNumberDisplay>
                                        </Paragraph>
                                    </Stack>
                                </Row>
                                <RichText html={dock.description} />
                            </Stack>
                        </Column>
                        <Column width="1/2" horizontal="right">
                            <Stack>
                                <CleanLinkButton
                                    href={mapsUrl}
                                    target="_blank"
                                    disableInertia
                                    fullWidth
                                >
                                    <StaticGoogleMap
                                        apiKey={googleApiKey}
                                        width={247}
                                        height={194}
                                        zoom={15}
                                        gps={dock.address.gps}
                                    />
                                </CleanLinkButton>
                                <Inline horizontal="right">
                                    <LinkButton
                                        variant="subtle"
                                        href={mapsUrl}
                                        target="_blank"
                                        disableInertia
                                    >
                                        {trans('MapsButton')}
                                    </LinkButton>
                                </Inline>
                            </Stack>
                        </Column>
                    </Columns>
                </PaddedBox>
                <PaddedBox
                    variant="tint"
                    borderRadius="bottom"
                    verticalPadding="small"
                    horizontal="right"
                >
                    <Button onClick={handleChooseClick}>
                        {trans('ChooseButton')}
                    </Button>
                </PaddedBox>
            </Card>
        </DialogModal>
    );
};

export default DockInfoModal;
